/**
 * Leasing Pipeline API Client — Landlord Portal
 *
 * All leasing pipeline endpoints under /landlord/api/listings/*
 * Uses credentials: 'include' and throws on non-ok responses.
 *
 * Phase 1: Listing CRUD + lifecycle, screening criteria, availability rules/exceptions.
 * Phase 1.5: Jurisdiction constraints + deposit/fee/utility fields.
 */

import { z } from 'zod'
import {
  LEASE_TERM_LABELS as SHARED_LEASE_TERM_LABELS,
  UTILITY_LABELS as SHARED_UTILITY_LABELS,
  FURNISHED_LABELS as SHARED_FURNISHED_LABELS,
} from '@resida/shared'
import {
  ListingResponseSchema,
  ListingStatusSchema,
  smokingPolicySchema,
  AvailabilityExceptionKindSchema,
  PetTypeSchema,
  MaxPetSizeSchema,
  LeadStageSchema,
  LeadDetailResponseSchema,
  ConstraintWarningSchema,
  ListingConstraintsSchema,
  AvailabilityRuleResponseSchema,
  AvailabilityExceptionResponseSchema,
  ListListingsResponseSchema,
  GetListingResponseSchema,
  CreateListingBodySchema,
  UpdateListingBodySchema,
  CreateRuleBodySchema,
  UpdateRuleBodySchema,
  CreateExceptionBodySchema,
  UnitListItemSchema,
  ChannelMessageResponseSchema,
  ListLeadsResponseSchema,
  CreateLeadBodySchema,
  UpdateLeadBodySchema,
  ListAllLeadsQuerySchema,
  SendReplyAttachmentSchema,
  ShowingAttendeeResponseSchema,
  ShowingAppointmentResponseSchema,
  ScheduleAppointmentBodySchema,
  UpdateAppointmentBodySchema,
  VerificationAttachmentResponseSchema,
  VerificationTaskResponseSchema,
  ProposalResultSchema,
  CreateReferenceCheckBodySchema,
  RefereeStatusViewSchema,
  GetReferenceCheckResponseSchema,
  ApplicationItemSchema,
  DecideLeadBodySchema,
  SweepResultSchema,
  RejectionNoticeBodySchema,
  type ScreeningCriteriaResponse,
  type UpsertScreeningCriteriaBody,
  type UpcomingShowingItem as UpcomingShowingItemContract,
  type SendApplicationLinkResponse as SendApplicationLinkResponseContract,
  type SendInitialApprovalResponse as SendInitialApprovalResponseContract,
  type UnreviewedApplicationsCountResponse,
  type PublishQuoteResponse,
  type ActivateListingResponse,
  type RenewListingResponse,
  type ActivateListingBody,
  type RenewListingBody,
  type ListingPreviewResponse,
} from '@resida/api-types/leasing'
import { apiErrorFromBody } from '../api/billing'
import { triggerReauthRequired } from './api.core'
import { readFileIntoMemory } from '@resida/shared';

// ---------------------------------------------------------------------------
// Lease term canonical values (mirrors listing.types.ts on the server)
// ---------------------------------------------------------------------------

export const ACCEPTED_LEASE_TERMS = ['month_to_month', '6_months', '12_months', '24_months'] as const
export type LeaseTermOption = (typeof ACCEPTED_LEASE_TERMS)[number]

// Sourced from @resida/shared (single source of truth, shared with the prospect public page).
export const LEASE_TERM_LABELS = SHARED_LEASE_TERM_LABELS as Record<LeaseTermOption, string>

// ---------------------------------------------------------------------------
// Utility / furnished canonical values (Phase 1.5)
// ---------------------------------------------------------------------------

export const UTILITY_TYPES = ['water', 'sewer', 'trash', 'gas', 'electric', 'heat', 'cooling', 'internet', 'cable'] as const
export type UtilityType = (typeof UTILITY_TYPES)[number]

export const UTILITY_LABELS = SHARED_UTILITY_LABELS as Record<UtilityType, string>

export const FURNISHED_OPTIONS = ['unfurnished', 'partial', 'furnished'] as const
export type FurnishedOption = (typeof FURNISHED_OPTIONS)[number]

export const FURNISHED_LABELS = SHARED_FURNISHED_LABELS as Record<FurnishedOption, string>

// ---------------------------------------------------------------------------
// Jurisdiction constraint types (Phase 1.5)
// ---------------------------------------------------------------------------

// Derived from ConstraintWarningSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-listings routes serialize the response through.
export type ConstraintWarning = z.infer<typeof ConstraintWarningSchema>;

// Derived from ListingConstraintsSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-listings jurisdiction-constraints route serializes through.
export type ListingConstraints = z.infer<typeof ListingConstraintsSchema>;

// ---------------------------------------------------------------------------
// Types — derived from @resida/api-types/leasing's Zod schemas (the boundary contract),
// not hand-declared, so a wire-value drift is a typecheck error rather than a runtime
// mismatch. See packages/api-types/README.md.
// ---------------------------------------------------------------------------

export type ListingStatus = z.infer<typeof ListingStatusSchema>;
export type SmokingPolicy = z.infer<typeof smokingPolicySchema>;
export type AvailabilityExceptionKind = z.infer<typeof AvailabilityExceptionKindSchema>;

// ---------------------------------------------------------------------------
// Typed-JSON bags (mirrors listing.types.ts on the server — ADR-LS-008)
// ---------------------------------------------------------------------------

export type PetType = z.infer<typeof PetTypeSchema>;
export type MaxPetSize = z.infer<typeof MaxPetSizeSchema>;

export interface ListingFees {
  securityDepositCents?: number | null;
  applicationFeeCents?: number | null;
  petDepositCents?: number | null;
  petRentMonthlyCents?: number | null;
  firstMonthRequired?: boolean;
  lastMonthRequired?: boolean;
  administrationFeeCents?: number | null;
  holdingDepositCents?: number | null;
  parkingFeeMonthlyCents?: number | null;
}

export interface ListingPetPolicy {
  petsAllowed: boolean;
  petTypesAllowed?: PetType[];
  maxPetSize?: MaxPetSize | null;
}

export interface ListingIncomeRestrictions {
  onePersonCents?: number | null;
  twoPersonCents?: number | null;
  threePersonCents?: number | null;
  fourPersonCents?: number | null;
  fivePersonCents?: number | null;
  sixPersonCents?: number | null;
  sevenPersonCents?: number | null;
  eightPersonCents?: number | null;
}

// Derived from ListingResponseSchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-listings routes serialize the response through, so field-name drift is
// a typecheck error rather than something a contract test has to approximate at runtime.
export type Listing = z.infer<typeof ListingResponseSchema>;

// Derived from ScreeningCriteriaResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-listings routes serialize the response through, so
// field-name drift is a typecheck error rather than something a contract test has to
// approximate at runtime.
export type ScreeningCriteria = ScreeningCriteriaResponse;

// Derived from AvailabilityRuleResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-listings availability routes serialize through.
export type AvailabilityRule = z.infer<typeof AvailabilityRuleResponseSchema>;

// Derived from AvailabilityExceptionResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-listings availability routes serialize through.
export type AvailabilityException = z.infer<typeof AvailabilityExceptionResponseSchema>;

// Derived from UnitListItemSchema in @resida/api-types/leasing — the same schema object
// apps/api's unit routes serialize the response through.
export type UnitListItem = z.infer<typeof UnitListItemSchema>;

// Derived from ListListingsResponseSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-listings routes serialize the response through.
export type ListingsResponse = z.infer<typeof ListListingsResponseSchema>;

// Derived from GetListingResponseSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-listings routes serialize the response through. contactPhone/
// contactName (Iteration 6, listing-syndication) are carried as fields of `listing` itself
// via ListingResponseSchema, not top-level siblings here.
export type ListingDetailResponse = z.infer<typeof GetListingResponseSchema>;

export interface AvailabilityResponse {
  rules: AvailabilityRule[];
  exceptions: AvailabilityException[];
}

export interface UnitsResponse {
  units: UnitListItem[];
  total: number;
}

// ---------------------------------------------------------------------------
// Create/Update param types
// ---------------------------------------------------------------------------

// Derived from CreateListingBodySchema in @resida/api-types/leasing — z.input (not
// z.infer) because utilitiesIncluded/furnished/requireRentersInsurance carry `.default(...)`,
// which z.infer's output type would wrongly require on every caller.
export type CreateListingParams = z.input<typeof CreateListingBodySchema>;

// Derived from UpdateListingBodySchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-listings routes validate the request body through. Includes
// contactPhone/contactName (Iteration 6, listing-syndication, BR-LSI6-01/02).
export type UpdateListingParams = z.infer<typeof UpdateListingBodySchema>;

// Derived from UpsertScreeningCriteriaBodySchema in @resida/api-types/leasing — see the
// ScreeningCriteria comment above.
export type UpsertScreeningCriteriaParams = UpsertScreeningCriteriaBody;

// Derived from CreateRuleBodySchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-listings availability routes validate the request body through.
export type CreateAvailabilityRuleParams = z.infer<typeof CreateRuleBodySchema>;

// Derived from UpdateRuleBodySchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-listings availability routes validate the request body through.
export type UpdateAvailabilityRuleParams = z.infer<typeof UpdateRuleBodySchema>;

// Derived from CreateExceptionBodySchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-listings availability routes validate the request body through.
export type CreateAvailabilityExceptionParams = z.infer<typeof CreateExceptionBodySchema>;

export interface UpdateAvailabilityExceptionParams {
  startTime?: string | null;
  endTime?: string | null;
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// Base fetch helper
// ---------------------------------------------------------------------------

async function leasingFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/landlord/api${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    // ApiClientError keeps the machine-readable `code` plus the payment fields
    // (`declineCode`, `quote`) of the paid activate/renew routes; `status` and `message`
    // behave exactly as the plain Error this used to throw.
    const body: unknown = await response.json().catch(() => ({}));
    // Publishing / renewing a listing charges the saved card and needs a fresh session: hand off to
    // the shared re-authentication flow (which returns here) exactly as the add-on enable call does.
    if (response.status === 403 && (body as { code?: string } | null)?.code === 'REAUTHENTICATION_REQUIRED') {
      triggerReauthRequired();
      return new Promise<never>(() => {});
    }
    throw apiErrorFromBody(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

export async function fetchUnits(params: {
  propertyId?: string;
  portfolioId?: string;
} = {}): Promise<UnitsResponse> {
  const qs = new URLSearchParams();
  if (params.propertyId) qs.set('propertyId', params.propertyId);
  if (params.portfolioId) qs.set('portfolioId', params.portfolioId);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return leasingFetch<UnitsResponse>(`/units${query}`);
}

// ---------------------------------------------------------------------------
// Jurisdiction Constraints (Phase 1.5)
// ---------------------------------------------------------------------------

export async function fetchJurisdictionConstraints(
  unitId: string
): Promise<{ constraints: ListingConstraints }> {
  return leasingFetch<{ constraints: ListingConstraints }>(
    `/listings/jurisdiction-constraints?unitId=${encodeURIComponent(unitId)}`
  );
}

// ---------------------------------------------------------------------------
// Listings
// ---------------------------------------------------------------------------

export async function fetchListings(params: {
  status?: ListingStatus[];
  unitId?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<ListingsResponse> {
  const qs = new URLSearchParams();
  // AC-8: `status` must be PRESENT (even as `?status=`) whenever the caller passed an
  // array at all — an empty array is the explicit "zero statuses selected" state
  // (`{ in: [] }`, zero rows) and must be distinguishable on the wire from `undefined`
  // ("no filter", all statuses) per ListListingsQuerySchema's documented contract. A
  // `.length > 0` guard here would silently collapse both cases to "no filter".
  if (params.status !== undefined) qs.set('status', params.status.join(','));
  if (params.unitId) qs.set('unitId', params.unitId);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return leasingFetch<ListingsResponse>(`/listings${query}`);
}

export async function createListing(
  params: CreateListingParams
): Promise<{ listing: Listing; warnings: ConstraintWarning[] }> {
  return leasingFetch<{ listing: Listing; warnings: ConstraintWarning[] }>('/listings', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function fetchListing(id: string): Promise<ListingDetailResponse> {
  return leasingFetch<ListingDetailResponse>(`/listings/${encodeURIComponent(id)}`);
}

/**
 * A draft Listing exactly as prospects will see it (BR-108, BR-109): the public projection, saved data
 * only. 404 for an unknown listing, a non-draft one, or one the caller cannot view.
 */
export async function fetchListingPreview(id: string): Promise<ListingPreviewResponse> {
  return leasingFetch<ListingPreviewResponse>(`/listings/${encodeURIComponent(id)}/preview`);
}

export async function updateListing(
  id: string,
  params: UpdateListingParams
): Promise<{ listing: Listing; warnings: ConstraintWarning[] }> {
  return leasingFetch<{ listing: Listing; warnings: ConstraintWarning[] }>(
    `/listings/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(params),
    }
  );
}

/** DC-48 — what publishing (or republishing) this listing will cost, and the card on file. */
export async function getPublishQuote(id: string, idempotencyKey?: string): Promise<PublishQuoteResponse> {
  // With the caller's persisted key the server returns the PINNED quote of an unresolved attempt (BR-91).
  const query = idempotencyKey ? `?idempotencyKey=${encodeURIComponent(idempotencyKey)}` : '';
  return leasingFetch<PublishQuoteResponse>(`/listings/${encodeURIComponent(id)}/publish-quote${query}`);
}

export async function activateListing(
  id: string,
  body: ActivateListingBody
): Promise<ActivateListingResponse> {
  return leasingFetch<ActivateListingResponse>(
    `/listings/${encodeURIComponent(id)}/activate`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

/** Pay for another listing period, queued to start when the current one ends. */
export async function renewListing(
  id: string,
  body: RenewListingBody
): Promise<RenewListingResponse> {
  return leasingFetch<RenewListingResponse>(
    `/listings/${encodeURIComponent(id)}/renew`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export async function pauseListing(id: string): Promise<{ listing: Listing }> {
  return leasingFetch<{ listing: Listing }>(`/listings/${encodeURIComponent(id)}/pause`, {
    method: 'POST',
  });
}

export async function closeListing(
  id: string,
  opts?: { notifyOpenLeads?: boolean; skipLeadCascade?: boolean }
): Promise<{ listing: Listing }> {
  return leasingFetch<{ listing: Listing }>(`/listings/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    body: JSON.stringify({
      notifyOpenLeads: opts?.notifyOpenLeads,
      skipLeadCascade: opts?.skipLeadCascade,
    }),
  });
}

// `Listing` here is now derived from @resida/api-types/leasing's ListingResponseSchema (see
// above) — matches the upstream contract (leasing-listings.schemas.ts's
// ReopenListingResponseSchema) exactly, verified at typecheck time.
export async function reopenListing(id: string): Promise<{ listing: Listing }> {
  return leasingFetch<{ listing: Listing }>(`/listings/${encodeURIComponent(id)}/reopen`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Screening Criteria
// ---------------------------------------------------------------------------

export async function fetchScreeningCriteria(
  listingId: string
): Promise<{ screeningCriteria: ScreeningCriteria | null }> {
  const detail = await leasingFetch<ListingDetailResponse>(
    `/listings/${encodeURIComponent(listingId)}`
  );
  return { screeningCriteria: detail.screeningCriteria };
}

export async function upsertScreeningCriteria(
  listingId: string,
  params: UpsertScreeningCriteriaParams
): Promise<{ screeningCriteria: ScreeningCriteria; warnings: ConstraintWarning[] }> {
  return leasingFetch<{ screeningCriteria: ScreeningCriteria; warnings: ConstraintWarning[] }>(
    `/listings/${encodeURIComponent(listingId)}/screening-criteria`,
    { method: 'PUT', body: JSON.stringify(params) }
  );
}

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------

export async function fetchAvailability(listingId: string): Promise<AvailabilityResponse> {
  return leasingFetch<AvailabilityResponse>(
    `/listings/${encodeURIComponent(listingId)}/availability`
  );
}

export async function createAvailabilityRule(
  listingId: string,
  params: CreateAvailabilityRuleParams
): Promise<{ rule: AvailabilityRule }> {
  return leasingFetch<{ rule: AvailabilityRule }>(
    `/listings/${encodeURIComponent(listingId)}/availability/rules`,
    { method: 'POST', body: JSON.stringify(params) }
  );
}

export async function updateAvailabilityRule(
  listingId: string,
  ruleId: string,
  params: UpdateAvailabilityRuleParams
): Promise<{ rule: AvailabilityRule }> {
  return leasingFetch<{ rule: AvailabilityRule }>(
    `/listings/${encodeURIComponent(listingId)}/availability/rules/${encodeURIComponent(ruleId)}`,
    { method: 'PATCH', body: JSON.stringify(params) }
  );
}

export async function deleteAvailabilityRule(listingId: string, ruleId: string): Promise<void> {
  return leasingFetch<void>(
    `/listings/${encodeURIComponent(listingId)}/availability/rules/${encodeURIComponent(ruleId)}`,
    { method: 'DELETE' }
  );
}

export async function createAvailabilityException(
  listingId: string,
  params: CreateAvailabilityExceptionParams
): Promise<{ exception: AvailabilityException }> {
  return leasingFetch<{ exception: AvailabilityException }>(
    `/listings/${encodeURIComponent(listingId)}/availability/exceptions`,
    { method: 'POST', body: JSON.stringify(params) }
  );
}

export async function updateAvailabilityException(
  listingId: string,
  exceptionId: string,
  params: UpdateAvailabilityExceptionParams
): Promise<{ exception: AvailabilityException }> {
  return leasingFetch<{ exception: AvailabilityException }>(
    `/listings/${encodeURIComponent(listingId)}/availability/exceptions/${encodeURIComponent(exceptionId)}`,
    { method: 'PATCH', body: JSON.stringify(params) }
  );
}

export async function deleteAvailabilityException(
  listingId: string,
  exceptionId: string
): Promise<void> {
  return leasingFetch<void>(
    `/listings/${encodeURIComponent(listingId)}/availability/exceptions/${encodeURIComponent(exceptionId)}`,
    { method: 'DELETE' }
  );
}

// ---------------------------------------------------------------------------
// Leads — Phase 2
// ---------------------------------------------------------------------------

// Derived from LeadStageSchema in @resida/api-types/leasing — the same schema the
// boundary's Lead response/query schemas use.
export type LeadStage = z.infer<typeof LeadStageSchema>;

/** All valid lead decision values in the database (including system-only no_decision). */
export type LeadDecision =
  | 'approved_lease_started'
  | 'withdrawn'
  | 'rejected'
  | 'no_decision';

/** Landlord-selectable decision values — excludes the system-only no_decision value. */
export type LandlordSelectableLeadDecision =
  | 'approved_lease_started'
  | 'withdrawn'
  | 'rejected';

export type LeadRejectionBasis =
  | 'procedural_listing_closed'
  | 'procedural_other_applicant'
  | 'substantive_consumer_report'
  | 'substantive_employment'
  | 'substantive_rental_history'
  | 'substantive_other';

export const REJECTION_BASIS_LABELS: Record<LeadRejectionBasis, string> = {
  procedural_listing_closed: 'Listing no longer available',
  procedural_other_applicant: 'Another applicant was selected',
  substantive_consumer_report: 'Based on a background check or consumer report',
  substantive_employment: 'Based on employment or income verification',
  substantive_rental_history: 'Based on rental history from a prior landlord',
  substantive_other: 'Other reason',
};

export const REJECTION_BASIS_NOTICE_TEXT: Record<LeadRejectionBasis, string> = {
  procedural_listing_closed: 'The listing is no longer available.',
  procedural_other_applicant: 'Another applicant was selected for this unit.',
  substantive_consumer_report: 'Information obtained from a consumer reporting agency (background or credit check).',
  substantive_employment: 'Information about your employment or income history.',
  substantive_rental_history: 'Information about your rental history.',
  substantive_other: 'Other information considered in evaluating your application.',
};

export interface Lead {
  id: string;
  listingId: string;
  landlordScopeId: string;
  prospectName: string | null;
  prospectEmail: string;
  prospectPhone: string | null;
  stage: LeadStage;
  decision: LeadDecision | null;
  decisionNote: string | null;
  decidedAt: string | null;
  decidedByUserId: string | null;
  claimedMonthlyIncomeCents: number | null;
  claimedHouseholdSize: number | null;
  claimedPets: number | null;
  claimedMoveInDate: string | null;
  claimedNotes: string | null;
  source: string | null;
  legalHold: boolean;
  dataDisposedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
  // Location fields
  propertyAddress: string | null;
  unitLabel: string | null;
  // Read state
  hasUnread: boolean;
  // Rejection fields (Phase 3 — landlord compliance)
  rejectionBasis: LeadRejectionBasis | null;
  rejectionNoticeSentAt: string | null;
  // Initial approval fields (US-IA-1)
  initialApprovalSentAt: string | null;
  initialApprovalSentByUserId: string | null;
}

// Derived from ChannelMessageResponseSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-leads route serializes the lead detail response through.
export type LeadMessage = z.infer<typeof ChannelMessageResponseSchema>;

// Derived from LeadDetailResponseSchema in @resida/api-types/leasing — the same schema object
// apps/api's GET /leads/:id route serializes the lead through.
export type LeadDetail = z.infer<typeof LeadDetailResponseSchema>;

// A case event on the lead detail response.
export type LeadEvent = LeadDetail['events'][number];

// Derived from ListLeadsResponseSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-leads routes serialize the response through.
export type LeadsResponse = z.infer<typeof ListLeadsResponseSchema>;

export interface LeadDetailResponse {
  lead: LeadDetail;
}

// Derived from CreateLeadBodySchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-leads routes validate the request body through.
export type CreateLeadParams = z.infer<typeof CreateLeadBodySchema>;

// Derived from UpdateLeadBodySchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-leads routes validate the request body through.
export type UpdateLeadParams = z.infer<typeof UpdateLeadBodySchema>;

// Derived from ListAllLeadsQuerySchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-leads cross-listing index route validates the querystring
// through. That schema parses an incoming HTTP querystring (page/pageSize default via
// z.coerce.number().default(...), unrespondedOnly is a 'true'/'false' string transformed
// to boolean), which is not quite this client-side "params to build a query string from"
// shape — z.infer's output type is used here since it's the closer match (unrespondedOnly
// resolves to boolean, matching existing call sites), but page/pageSize become required
// numbers post-default even though every caller here still treats them as optional.
// TODO(api-types-922): ListAllLeadsQuerySchema is a request-parsing schema (coercion +
// defaults); consider a dedicated client-side query-params type if this drifts further.
export type ListLeadsParams = Partial<z.infer<typeof ListAllLeadsQuerySchema>>;

// This endpoint (GET /listings/:id/leads) is served by ListLeadsByListingQuerySchema,
// which intentionally stays single-value `stage` (unlike ListAllLeadsQuerySchema below) —
// so `stage` is narrowed back to a single LeadStage here rather than inheriting the
// array type ListLeadsParams now carries.
export async function fetchListingLeads(
  listingId: string,
  params: Omit<ListLeadsParams, 'listingId' | 'stage'> & { stage?: LeadStage } = {}
): Promise<LeadsResponse> {
  const qs = new URLSearchParams();
  if (params.stage) qs.set('stage', params.stage);
  if (params.unrespondedOnly) qs.set('unrespondedOnly', 'true');
  if (params.since) qs.set('since', params.since);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return leasingFetch<LeadsResponse>(`/listings/${encodeURIComponent(listingId)}/leads${query}`);
}

export async function createLead(
  listingId: string,
  params: CreateLeadParams
): Promise<{ lead: Lead }> {
  return leasingFetch<{ lead: Lead }>(`/listings/${encodeURIComponent(listingId)}/leads`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function fetchLeads(params: ListLeadsParams = {}): Promise<LeadsResponse> {
  const qs = new URLSearchParams();
  if (params.listingId) qs.set('listingId', params.listingId);
  // AC-8: same undefined-vs-empty-array distinction as fetchListings above — `stage`
  // must be sent (as `?stage=`) whenever an array was passed, per
  // ListAllLeadsQuerySchema's documented contract.
  if (params.stage !== undefined) qs.set('stage', params.stage.join(','));
  if (params.unrespondedOnly) qs.set('unrespondedOnly', 'true');
  if (params.since) qs.set('since', params.since);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return leasingFetch<LeadsResponse>(`/leads${query}`);
}

export async function fetchLead(leadId: string): Promise<LeadDetailResponse> {
  return leasingFetch<LeadDetailResponse>(`/leads/${encodeURIComponent(leadId)}`);
}

export async function updateLead(
  leadId: string,
  params: UpdateLeadParams
): Promise<{ lead: Lead }> {
  return leasingFetch<{ lead: Lead }>(`/leads/${encodeURIComponent(leadId)}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  });
}

export async function advanceLeadStage(
  leadId: string,
  to: LeadStage
): Promise<{ lead: Lead }> {
  return leasingFetch<{ lead: Lead }>(`/leads/${encodeURIComponent(leadId)}/advance`, {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

// Derived from SendReplyAttachmentSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-leads message route serializes the response through.
export type SendReplyAttachment = z.infer<typeof SendReplyAttachmentSchema>;

export interface SendReplyResponse {
  messageId: string;
  attachments: SendReplyAttachment[];
}

export async function sendLeadReply(
  leadId: string,
  content: string,
  files?: File[]
): Promise<SendReplyResponse> {
  // Use multipart/form-data when files are present so the backend can
  // receive both the text content and binary file parts in a single request.
  // Do NOT use leasingFetch here — it unconditionally sets Content-Type:
  // application/json when body is defined, which breaks multipart boundary parsing.
  if (files && files.length > 0) {
    const formData = new FormData();
    formData.append('content', content);
    for (const file of files) {
      formData.append('files', await readFileIntoMemory(file), file.name);
    }
    const response = await fetch(`/landlord/api/leads/${encodeURIComponent(leadId)}/messages`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // No Content-Type header — browser sets multipart/form-data boundary automatically
    });
    if (!response.ok) {
      let message = `${response.status} ${response.statusText}`;
      try {
        const err = await response.json() as { message?: string };
        if (err.message) message = err.message;
      } catch {
        // ignore parse error
      }
      const error = new Error(message) as Error & { status: number };
      error.status = response.status;
      throw error;
    }
    return response.json() as Promise<SendReplyResponse>;
  }

  // No files — still send as multipart so the backend (multipart-only route)
  // can parse it correctly. A single text field in a FormData body is valid.
  const formData = new FormData();
  formData.append('content', content);
  const response = await fetch(`/landlord/api/leads/${encodeURIComponent(leadId)}/messages`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch {
      // ignore parse error
    }
    const error = new Error(message) as Error & { status: number };
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<SendReplyResponse>;
}

export async function markLeadRead(leadId: string): Promise<void> {
  await fetch(`/landlord/api/leads/${encodeURIComponent(leadId)}/read`, {
    method: 'POST',
    credentials: 'include',
  });
  // Fire and forget — ignore errors silently
}

// ---------------------------------------------------------------------------
// Showings — Phase 3
// ---------------------------------------------------------------------------

export type ShowingKind = 'private' | 'open_house';
export type ShowingOutcome = 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
export type RsvpStatus = 'invited' | 'confirmed' | 'declined' | 'attended' | 'no_show';

// Derived from ShowingAttendeeResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-showings routes serialize the response through.
export type ShowingAttendee = z.infer<typeof ShowingAttendeeResponseSchema>;

// Derived from ShowingAppointmentResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-showings routes serialize the response through.
export type ShowingAppointment = z.infer<typeof ShowingAppointmentResponseSchema>;

export interface ShowingsResponse {
  items: ShowingAppointment[];
  /** IANA timezone for the listing unit. Display all times in this zone. */
  timezone: string;
}

// Derived from ScheduleAppointmentBodySchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-showings routes validate the request body through.
// Note: leadId is optional-but-not-nullable in the schema (unlike this type's prior
// hand-declared `string | null`) — no current caller passes null explicitly (only
// `undefined`), so the narrowed type is a no-op change.
export type ScheduleAppointmentParams = z.infer<typeof ScheduleAppointmentBodySchema>;

// Derived from UpdateAppointmentBodySchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-showings routes validate the request body through.
export type UpdateAppointmentParams = z.infer<typeof UpdateAppointmentBodySchema>;

export interface AddAttendeeParams {
  leadId: string;
}

export interface UpdateAttendeeParams {
  rsvpStatus?: RsvpStatus;
  notes?: string | null;
}

export async function fetchShowingsForListing(
  listingId: string,
  params: { dateFrom?: string; dateTo?: string } = {}
): Promise<ShowingsResponse> {
  const qs = new URLSearchParams();
  if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
  if (params.dateTo) qs.set('dateTo', params.dateTo);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return leasingFetch<ShowingsResponse>(
    `/listings/${encodeURIComponent(listingId)}/showings${query}`
  );
}

export async function scheduleAppointment(
  listingId: string,
  params: ScheduleAppointmentParams
): Promise<{ appointment: ShowingAppointment }> {
  return leasingFetch<{ appointment: ShowingAppointment }>(
    `/listings/${encodeURIComponent(listingId)}/showings`,
    { method: 'POST', body: JSON.stringify(params) }
  );
}

export async function updateAppointment(
  listingId: string,
  appointmentId: string,
  params: UpdateAppointmentParams
): Promise<{ appointment: ShowingAppointment }> {
  return leasingFetch<{ appointment: ShowingAppointment }>(
    `/listings/${encodeURIComponent(listingId)}/showings/${encodeURIComponent(appointmentId)}`,
    { method: 'PATCH', body: JSON.stringify(params) }
  );
}

export async function addAttendee(
  listingId: string,
  appointmentId: string,
  params: AddAttendeeParams
): Promise<{ attendee: ShowingAttendee }> {
  return leasingFetch<{ attendee: ShowingAttendee }>(
    `/listings/${encodeURIComponent(listingId)}/showings/${encodeURIComponent(appointmentId)}/attendees`,
    { method: 'POST', body: JSON.stringify(params) }
  );
}

export async function updateAttendee(
  listingId: string,
  appointmentId: string,
  attendeeId: string,
  params: UpdateAttendeeParams
): Promise<{ attendee: ShowingAttendee }> {
  return leasingFetch<{ attendee: ShowingAttendee }>(
    `/listings/${encodeURIComponent(listingId)}/showings/${encodeURIComponent(appointmentId)}/attendees/${encodeURIComponent(attendeeId)}`,
    { method: 'PATCH', body: JSON.stringify(params) }
  );
}

export async function removeAttendee(
  listingId: string,
  appointmentId: string,
  attendeeId: string
): Promise<void> {
  return leasingFetch<void>(
    `/listings/${encodeURIComponent(listingId)}/showings/${encodeURIComponent(appointmentId)}/attendees/${encodeURIComponent(attendeeId)}`,
    { method: 'DELETE' }
  );
}

// ---------------------------------------------------------------------------
// Inbox — Phase 2
// ---------------------------------------------------------------------------

export interface UnrespondedMessage {
  leadId: string;
  listingId: string;
  propertyAddress: string | null;
  unitLabel: string | null;
  leadStage: string;
  prospectEmail: string;
  prospectName: string | null;
  lastInboundAt: string;
  inboundCount: number;
  isNewInquiry: boolean;
}

export interface InboxResponse {
  items: UnrespondedMessage[];
  totalCount: number;
}

export async function fetchInboxUnresponded(): Promise<InboxResponse> {
  return leasingFetch<InboxResponse>('/inbox/unresponded');
}

export async function fetchInboxUnrespondedCount(): Promise<{ count: number }> {
  return leasingFetch<{ count: number }>('/inbox/unresponded/count');
}

// leasing-support/BR-38 — Applications-tab nav badge count. Derived from
// GetUnreviewedApplicationsCountResponseSchema in @resida/api-types/leasing — the same
// schema object GET /landlord/api/applications/unreviewed/count (leasing-applicants.routes.ts)
// serializes the response through.
export async function fetchUnreviewedApplicationsCount(): Promise<UnreviewedApplicationsCountResponse> {
  return leasingFetch<UnreviewedApplicationsCountResponse>('/applications/unreviewed/count');
}

// ---------------------------------------------------------------------------
// Verifications — Phase 4 / Iteration 1 (applicant-scoped)
// ---------------------------------------------------------------------------

export type VerificationKind = 'criminal_civil' | 'income' | 'rental_history' | 'government_id' | 'supporting_documents';
export type VerificationStatus = 'pending' | 'requested' | 'in_progress' | 'passed' | 'failed' | 'waived';
export type UploadActorType = 'manager' | 'prospect';

// Derived from VerificationAttachmentResponseSchema in @resida/api-types/leasing — the
// same schema object apps/api's leasing-verification routes serialize the response through.
export type VerificationAttachment = z.infer<typeof VerificationAttachmentResponseSchema>;

export interface ActiveTokenPresence {
  hasActiveToken: boolean;
  expiresAt: string | null;
}

// Derived from VerificationTaskResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-verification routes serialize the response through.
export type VerificationTask = z.infer<typeof VerificationTaskResponseSchema>;

export interface UpdateVerificationTaskParams {
  status?: VerificationStatus;
  notes?: string | null;
}

// ---------------------------------------------------------------------------
// Applicants — Iteration 1
// ---------------------------------------------------------------------------

export type ApplicantRole = 'primary' | 'co_applicant' | 'guarantor';
export type ApplicantStatus = 'invited' | 'started' | 'submitted' | 'withdrawn';

// ---------------------------------------------------------------------------
// Shared applicant data (Iteration 2 — per-lead, shared across all applicants)
// ---------------------------------------------------------------------------

export interface PetEntry {
  type: string;
  name?: string | null;
  breed?: string | null;
  weightLbs?: number | null;
}

export interface OccupantEntry {
  name: string;
  relationship: string;
  dateOfBirth?: string | null;
}

export interface SharedApplicantData {
  applicationPets: PetEntry[] | null;
  applicationOccupants: OccupantEntry[] | null;
}

// ---------------------------------------------------------------------------
// JSON entry types for applicant claimed arrays (Iteration 2)
// ---------------------------------------------------------------------------

export interface ResidentialHistoryEntry {
  address: string;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  landlordName?: string | null;
  landlordPhone?: string | null;
  landlordEmail?: string | null;
  monthlyRentCents?: number | null;
  moveInDate?: string | null;
  moveOutDate?: string | null;
  reasonForLeaving?: string | null;
}

export interface OtherIncomeEntry {
  type: string;
  monthlyAmountCents: number;
}

export interface RecurringDebtEntry {
  type: string;
  monthlyPaymentCents: number;
}

export interface VehicleEntry {
  make?: string | null;
  model?: string | null;
  year?: number | null;
  licensePlate?: string | null;
  state?: string | null;
}

export interface PersonalReferenceEntry {
  name: string;
  relationship: string;
  phone: string;
  email?: string | null;
}

export interface EmergencyContactEntry {
  name: string;
  relationship: string;
  phone: string;
}

export interface CriminalDisclosureEntry {
  hasFelony: boolean;
  felonyExplanation?: string | null;
  hasMisdemeanor: boolean;
  misdemeanorExplanation?: string | null;
}

export interface Applicant {
  id: string;
  leadId: string;
  name: string;
  email: string;
  phone: string | null;
  role: ApplicantRole;
  status: ApplicantStatus;
  invitedAt: string;
  startedAt: string | null;
  submittedAt: string | null;
  withdrawnAt: string | null;
  // Iteration 5: computed household size (replaces claimed.claimedHouseholdSize)
  computedHouseholdSize: number;
  // Iteration 5: consent envelope for screening authorization
  consentEnvelope: {
    status: string;
    signedAt: string | null;
    downloadUrl: string | null;
  } | null;
  claimed: {
    // Iteration 1 fields
    claimedMonthlyIncomeCents: number | null;
    claimedPets: string | null;
    claimedMoveInDate: string | null;
    claimedEmployer: string | null;
    claimedNotes: string | null;
    // Iteration 2: identity
    claimedDateOfBirth?: string | null;
    claimedSsnLast4?: string | null;
    // Iteration 2: employment primary
    claimedPosition?: string | null;
    claimedEmployerStartDate?: string | null;
    claimedSupervisorName?: string | null;
    claimedSupervisorPhone?: string | null;
    // Iteration 2: employment previous
    claimedPreviousEmployer?: string | null;
    claimedPreviousEmployerPosition?: string | null;
    claimedPreviousEmployerStartDate?: string | null;
    claimedPreviousEmployerEndDate?: string | null;
    claimedPreviousEmployerMonthlyIncomeCents?: number | null;
    // Iteration 2: JSON arrays (cast to typed arrays before rendering)
    claimedResidentialHistory?: unknown | null;
    claimedOtherIncomeSources?: unknown | null;
    claimedRecurringDebts?: unknown | null;
    claimedVehicles?: unknown | null;
    claimedPersonalReferences?: unknown | null;
    claimedEmergencyContact?: unknown | null;
    claimedCriminalDisclosure?: unknown | null;
    // Iteration 2: flag
    claimedFirstTimeRenter?: boolean | null;
    // Iteration 9: income type
    claimedPrimaryIncomeType?: string | null;
  };
  verificationTasks: Array<{
    id: string;
    applicantId: string;
    kind: VerificationKind;
    status: VerificationStatus;
    attachmentCount: number;
  }>;
  activeInviteToken: { hasToken: boolean; expiresAt: string | null };
  // Iteration 2: shared household data (optional — not currently returned by list endpoint;
  // will render when API starts populating this field)
  sharedData?: SharedApplicantData | null;
}

export type ProposalStatus = 'pending' | 'confirmed' | 'rejected' | 'cancelled' | 'superseded';

// Derived from ProposalResultSchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-proposals routes serialize the response through.
export type AppointmentProposal = z.infer<typeof ProposalResultSchema>;

// ---------------------------------------------------------------------------
// Applicant management API functions
// ---------------------------------------------------------------------------

export async function fetchApplicants(leadId: string): Promise<Applicant[]> {
  const result = await leasingFetch<{ applicants: Applicant[] }>(
    `/leads/${encodeURIComponent(leadId)}/applicants`
  );
  return result.applicants;
}

export async function addApplicants(
  leadId: string,
  entries: { name: string; email: string; role: ApplicantRole }[]
): Promise<Applicant[]> {
  const result = await leasingFetch<{ applicants: Applicant[] }>(
    `/leads/${encodeURIComponent(leadId)}/applicants`,
    {
      method: 'POST',
      body: JSON.stringify({ applicants: entries }),
    }
  );
  return result.applicants;
}

export async function regenerateApplicantInvite(
  leadId: string,
  applicantId: string
): Promise<{ tokenUrl: string; expiresAt: string }> {
  return leasingFetch<{ tokenUrl: string; expiresAt: string }>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/regenerate-invite`,
    { method: 'POST' }
  );
}

export async function revokeApplicantInvite(
  leadId: string,
  applicantId: string
): Promise<void> {
  return leasingFetch<void>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/invite`,
    { method: 'DELETE' }
  );
}

export async function withdrawApplicant(
  leadId: string,
  applicantId: string
): Promise<void> {
  return leasingFetch<void>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/withdraw`,
    { method: 'POST' }
  );
}

// leasing-support/BR-38, BR-AT-18/BR-37/BR-AT-20 — mark a submitted applicant's
// application reviewed. Idempotent on the server (repeat calls after reviewedAt is
// already set are a no-op) — callers may fire this without checking prior state.
export async function markApplicantReviewed(
  leadId: string,
  applicantId: string
): Promise<void> {
  return leasingFetch<void>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/review`,
    { method: 'POST' }
  );
}

export async function mintScheduleToken(
  leadId: string
): Promise<{ tokenUrl: string; expiresAt: string }> {
  return leasingFetch<{ tokenUrl: string; expiresAt: string }>(
    `/leads/${encodeURIComponent(leadId)}/schedule-token`,
    { method: 'POST' }
  );
}

export async function revokeScheduleToken(leadId: string): Promise<void> {
  return leasingFetch<void>(
    `/leads/${encodeURIComponent(leadId)}/schedule-token`,
    { method: 'DELETE' }
  );
}

// Derived from SendApplicationLinkResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-applicants route serializes the response through, so
// field-name drift is a typecheck error rather than something a contract test has to
// approximate at runtime. On a 4xx (e.g. LEAD_CLOSED / LEAD_MISSING_PROSPECT_NAME),
// leasingFetch() throws an Error whose .message is the server's `message` field — callers
// show it inline.
export type SendApplicationLinkResponse = SendApplicationLinkResponseContract;

export async function sendApplicationLink(
  leadId: string
): Promise<SendApplicationLinkResponse> {
  return leasingFetch<SendApplicationLinkResponse>(
    `/leads/${encodeURIComponent(leadId)}/send-application-link`,
    { method: 'POST' }
  );
}

// Derived from SendInitialApprovalResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-leads route serializes the response through, so
// field-name drift is a typecheck error rather than something a contract test has to
// approximate at runtime. Other Lead-family response types in this file (DecideLeadResponse,
// reopenLead's `{ lead: Lead }`, updateLead's response, etc.) remain hand-declared pending
// their own migration — a pre-existing, family-wide gap spanning ~10 endpoints, tracked
// separately and not fixed as a side effect of this endpoint.
export type SendInitialApprovalResponse = SendInitialApprovalResponseContract;

export async function sendInitialApproval(
  leadId: string
): Promise<SendInitialApprovalResponse> {
  return leasingFetch<SendInitialApprovalResponse>(
    `/leads/${encodeURIComponent(leadId)}/send-initial-approval`,
    { method: 'POST' }
  );
}

// ---------------------------------------------------------------------------
// Proposals API functions
// ---------------------------------------------------------------------------

export async function fetchProposals(leadId: string): Promise<AppointmentProposal[]> {
  const result = await leasingFetch<{ proposals: AppointmentProposal[] }>(
    `/leads/${encodeURIComponent(leadId)}/proposals`
  );
  return result.proposals;
}

export async function confirmProposal(
  leadId: string,
  proposalId: string
): Promise<AppointmentProposal> {
  const result = await leasingFetch<{ proposal: AppointmentProposal }>(
    `/leads/${encodeURIComponent(leadId)}/proposals/${encodeURIComponent(proposalId)}/confirm`,
    { method: 'POST' }
  );
  return result.proposal;
}

export async function rejectProposal(
  leadId: string,
  proposalId: string,
  responseNotes?: string
): Promise<AppointmentProposal> {
  const result = await leasingFetch<{ proposal: AppointmentProposal }>(
    `/leads/${encodeURIComponent(leadId)}/proposals/${encodeURIComponent(proposalId)}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({ responseNotes: responseNotes ?? null }),
    }
  );
  return result.proposal;
}

// ---------------------------------------------------------------------------
// Verification functions — now applicant-scoped (Iteration 1)
// ---------------------------------------------------------------------------

export async function fetchApplicantVerificationTasks(
  leadId: string,
  applicantId: string
): Promise<{ tasks: VerificationTask[] }> {
  return leasingFetch<{ tasks: VerificationTask[] }>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/verifications`
  );
}

export async function updateVerificationTask(
  leadId: string,
  applicantId: string,
  taskId: string,
  params: UpdateVerificationTaskParams
): Promise<{ task: VerificationTask }> {
  return leasingFetch<{ task: VerificationTask }>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/verifications/${encodeURIComponent(taskId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(params),
    }
  );
}

export async function uploadVerificationAttachment(
  leadId: string,
  applicantId: string,
  taskId: string,
  file: File
): Promise<{ attachment: VerificationAttachment }> {
  const formData = new FormData();
  formData.append('files', await readFileIntoMemory(file), file.name);
  const response = await fetch(
    `/landlord/api/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/verifications/${encodeURIComponent(taskId)}/attachments`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    const error = new Error(message) as Error & { status: number };
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<{ attachment: VerificationAttachment }>;
}

export async function downloadVerificationAttachment(
  leadId: string,
  applicantId: string,
  taskId: string,
  attId: string
): Promise<string> {
  const response = await fetch(
    `/landlord/api/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/verifications/${encodeURIComponent(taskId)}/attachments/${encodeURIComponent(attId)}/download`,
    { credentials: 'include' }
  );
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export async function deleteVerificationAttachment(
  leadId: string,
  applicantId: string,
  taskId: string,
  attId: string
): Promise<void> {
  return leasingFetch<void>(
    `/leads/${encodeURIComponent(leadId)}/applicants/${encodeURIComponent(applicantId)}/verifications/${encodeURIComponent(taskId)}/attachments/${encodeURIComponent(attId)}`,
    { method: 'DELETE' }
  );
}

// ---------------------------------------------------------------------------
// Reference Check functions (DC-17) — landlord-triggered, read-only status view.
// Field names mirror apps/api/src/routes/manager/leasing-reference-check.schemas.ts
// exactly (RefereeStatusSchema, CreateReferenceCheckResponseSchema,
// GetReferenceCheckResponseSchema) — a direct z.infer import is not used because
// apps/landlord cannot import apps/api server-only route modules across the
// client/server package boundary; keep these hand-declared types byte-for-byte
// in sync with that schema file if it changes.
// ---------------------------------------------------------------------------

// Must match RefereeStatusSchema in apps/api/src/routes/manager/leasing-reference-check.schemas.ts
export type RefereeStatus = 'pending' | 'contacted' | 'follow_up_sent' | 'responded' | 'exhausted';

export interface ReferenceCheckRefereeInput {
  email: string;
  relationship: string;
  context?: string;
}

// Derived from CreateReferenceCheckBodySchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-reference-check routes validate the request body through.
export type CreateReferenceCheckParams = z.infer<typeof CreateReferenceCheckBodySchema>;

export interface CreateReferenceCheckResponse {
  referenceCheckId: string;
  status: 'open';
  referees: Array<{
    id: string;
    email: string;
    relationship: string;
    status: RefereeStatus;
  }>;
}

// Content-minimized status view — never reply content, never referee context (BR-10/BR-12).
// Derived from RefereeStatusViewSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-reference-check routes serialize the response through.
export type RefereeStatusView = z.infer<typeof RefereeStatusViewSchema>;

// Derived from GetReferenceCheckResponseSchema in @resida/api-types/leasing — the same
// schema object apps/api's leasing-reference-check routes serialize the response through.
export type GetReferenceCheckResponse = z.infer<typeof GetReferenceCheckResponseSchema>;

export async function triggerReferenceCheck(
  leadId: string,
  params: CreateReferenceCheckParams
): Promise<CreateReferenceCheckResponse> {
  return leasingFetch<CreateReferenceCheckResponse>(
    `/leads/${encodeURIComponent(leadId)}/reference-checks`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    }
  );
}

export async function fetchReferenceCheck(
  leadId: string,
  referenceCheckId: string
): Promise<GetReferenceCheckResponse> {
  return leasingFetch<GetReferenceCheckResponse>(
    `/leads/${encodeURIComponent(leadId)}/reference-checks/${encodeURIComponent(referenceCheckId)}`
  );
}

// ---------------------------------------------------------------------------
// Applications — Iteration 7
// ---------------------------------------------------------------------------

// Derived from ApplicationItemSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-dashboard applications route serializes the response through.
export type ApplicationItem = z.infer<typeof ApplicationItemSchema>;

export type ActivityItem =
  | { type: 'message'; occurredAt: string; leadId: string; prospectName: string | null; prospectEmail: string; listingAddress: string; snippet: string }
  | { type: 'appointment_proposed'; occurredAt: string; leadId: string; prospectName: string | null; prospectEmail: string; listingAddress: string; proposedFor: string; timezone: string }
  | { type: 'appointment_rescheduled'; occurredAt: string; leadId: string; prospectName: string | null; prospectEmail: string; listingAddress: string; newProposedFor: string; timezone: string }
  | { type: 'application_submitted'; occurredAt: string; leadId: string; applicantName: string; listingAddress: string }

export async function fetchApplications(): Promise<{ items: ApplicationItem[]; totalCount: number }> {
  return leasingFetch('/applications')
}

export async function fetchDashboardActivity(since: string): Promise<{ items: ActivityItem[] }> {
  return leasingFetch(`/dashboard/activity?since=${encodeURIComponent(since)}`)
}

// ---------------------------------------------------------------------------
// Upcoming showings (leasing dashboard) — Change 16
//
// Derived from UpcomingShowingItemSchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-dashboard route serializes the response through. (The
// pre-existing ActivityItem type above is still not derived this way — out of scope
// here, but a real gap.) `timezone` mirrors ActivityItem's appointment_* variants — the
// display layer anchors `scheduledFor` to the listing's property timezone via
// formatShowingTime() in LeasingDashboardPage.tsx.
// ---------------------------------------------------------------------------

export type UpcomingShowingItem = UpcomingShowingItemContract;

export async function fetchUpcomingShowings(window: string): Promise<{ items: UpcomingShowingItem[] }> {
  return leasingFetch(`/dashboard/showings?window=${encodeURIComponent(window)}`)
}

// ---------------------------------------------------------------------------
// Decision and Retention — Phase 5
// ---------------------------------------------------------------------------

// Derived from DecideLeadBodySchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-leads decide route validates the request body through.
export type DecideLeadParams = z.infer<typeof DecideLeadBodySchema>;

export interface DecideLeadResponse {
  lead: Lead;
  siblingCount?: number;
  siblingIds?: string[];
}

export interface LegalHoldParams {
  enabled: boolean;
  note?: string;
}

// Derived from SweepResultSchema in @resida/api-types/leasing — the same schema object
// apps/api's leasing-admin retention sweep route serializes the response through.
export type SweepResult = z.infer<typeof SweepResultSchema>;

export async function decideLead(
  leadId: string,
  params: DecideLeadParams
): Promise<DecideLeadResponse> {
  return leasingFetch<DecideLeadResponse>(
    `/leads/${encodeURIComponent(leadId)}/decide`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    }
  );
}

export async function reopenLead(leadId: string): Promise<{ lead: Lead }> {
  return leasingFetch<{ lead: Lead }>(
    `/leads/${encodeURIComponent(leadId)}/reopen`,
    { method: 'POST' }
  );
}

export async function setLegalHold(
  leadId: string,
  params: LegalHoldParams
): Promise<{ lead: Lead }> {
  return leasingFetch<{ lead: Lead }>(
    `/leads/${encodeURIComponent(leadId)}/legal-hold`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    }
  );
}

export async function purgeLeadData(leadId: string): Promise<{ disposed: true }> {
  return leasingFetch<{ disposed: true }>(
    `/leads/${encodeURIComponent(leadId)}/purge-data`,
    {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    }
  );
}

// ---------------------------------------------------------------------------
// Rejection Notice — Phase 3 (landlord compliance)
// ---------------------------------------------------------------------------

// Derived from RejectionNoticeBodySchema in @resida/api-types/leasing — the same schema
// object apps/api's leasing-leads rejection-notice route validates the request body through.
export type RejectionNoticeParams = z.infer<typeof RejectionNoticeBodySchema>;

export interface RejectionNoticeSendResponse {
  messageId: string;
  subject: string;
}

export async function sendRejectionNotice(
  leadId: string,
  params?: RejectionNoticeParams
): Promise<RejectionNoticeSendResponse> {
  return leasingFetch<RejectionNoticeSendResponse>(
    `/leads/${encodeURIComponent(leadId)}/rejection-notice`,
    {
      method: 'POST',
      body: JSON.stringify(params ?? {}),
    }
  );
}

// ---------------------------------------------------------------------------
// Relay message retry — BR-DELIVERY-RETRY
// Schemas below mirror apps/api/src/routes/manager/relay-messages.schemas.ts
// (RelayMessageRetryResponseSchema / RelayMessageRetryConflictResponseSchema)
// field-for-field — a direct import is not used because apps/landlord cannot
// import apps/api server-only route modules across the client/server package
// boundary (same constraint as the reference-check types above). Declaring
// the schema here (rather than a bare interface) and parsing the actual
// response through it means drift from the server shape fails loudly at
// runtime instead of silently type-checking against a stale hand-written type.
// ---------------------------------------------------------------------------

// 'not_failed' is a BR-RESOLVE-ONCE-style silent no-op (200, not an error) —
// the row was not (or no longer) status:failed when the retry landed. Callers
// should treat it identically to 'retried': refresh and let the server's
// current state speak for itself, with no error surfaced.
const RelayMessageRetryResponseSchema = z.object({
  status: z.enum(['retried', 'not_failed']),
});

export type RelayMessageRetryResponse = z.infer<typeof RelayMessageRetryResponseSchema>;

// The 409 conflict body (RelayMessageRetryConflictResponseSchema) is a small,
// machine-readable shape — status + optional reason — not the generic
// ErrorResponseSchema `{ message }` shape leasingFetch's error path expects.
// Parse it explicitly so a policy-blocked retry surfaces a meaningful message
// instead of a bare "409 Conflict". A no-longer-eligible retry is NOT a
// conflict (see RelayMessageRetryResponse above) — only 'blocked' is.
const RelayMessageRetryConflictBodySchema = z.object({
  status: z.literal('blocked'),
  reason: z.string().optional(),
});

export type RelayMessageRetryConflictBody = z.infer<typeof RelayMessageRetryConflictBodySchema>;

// Landlord-facing copy for known outbound policy gate slugs (mirrors the
// gate.slug switch in apps/api/src/services/relay/policy-engine.ts). A
// gate's slug is an internal identifier, not authored user-facing copy, so
// retry-blocked errors map it to plain language here rather than surfacing
// it verbatim. Unrecognized slugs (a new gate added without updating this
// map) still get a legible space-separated fallback instead of a raw
// hyphenated identifier.
const OUTBOUND_POLICY_GATE_LABELS: Record<string, string> = {
  'recipient-quarantined': 'the recipient has had too many recent delivery failures',
  'recipient-opted-out': 'the recipient has opted out of messages',
  'budget-exhausted': 'this conversation has reached its messaging budget',
  'landlord-budget-exhausted': 'your account has reached its monthly messaging budget',
};

function humanizeOutboundPolicyGateReason(slug: string): string {
  return OUTBOUND_POLICY_GATE_LABELS[slug] ?? slug.replace(/-/g, ' ');
}

export async function retryMessage(channelMessageId: string): Promise<RelayMessageRetryResponse> {
  const response = await fetch(
    `/landlord/api/relay/messages/${encodeURIComponent(channelMessageId)}/retry`,
    { method: 'POST', credentials: 'include' }
  );

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json() as { message?: string } & Record<string, unknown>;
      const conflict = RelayMessageRetryConflictBodySchema.safeParse(body);
      if (response.status === 409 && conflict.success) {
        message = conflict.data.reason
          ? `Retry blocked: ${humanizeOutboundPolicyGateReason(conflict.data.reason)}.`
          : 'Retry blocked by an outbound policy.';
      } else if (typeof body.message === 'string') {
        message = body.message;
      }
    } catch {
      // ignore parse error
    }
    const error = new Error(message) as Error & { status: number };
    error.status = response.status;
    throw error;
  }

  return RelayMessageRetryResponseSchema.parse(await response.json());
}
